import { DAY_CLOSING_STATUS_LABELS, SHIFT_LEDGER_STATUS_LABELS } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import { Alert, Button, Card, DateInput, FormField, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { CloseDayDialog } from "../../../../features/shifts/day-end-dialogs.js";
import {
  ChecklistCard,
  DayHeaderCard,
  DayShiftsPanel,
  DayTotalsPanel,
  DayZReportsPanel,
} from "../../../../features/shifts/day-end-panels.js";
import {
  DAY_LEDGER_TONE,
  blockingChecks,
  defaultBusinessDate,
} from "../../../../features/shifts/day-end-values.js";
import {
  useBusinessDays,
  useDayChecklist,
  useShiftPermissions,
} from "../../../../features/shifts/queries.js";
import {
  NoStations,
  ShiftStationField,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../../features/shifts/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({ ...shiftsSearchShape, date: localDateText });

export const Route = createFileRoute("/_app/vardiya/gun-sonu/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DayEndPage,
});

/**
 * Gün sonu: kontrol listesi, günün ön izlemesi ve gün kilidi.
 *
 * Varsayılan iş günü, o istasyonun KİLİTLENMEMİŞ EN ESKİ günüdür (kapanış sırayla yapılır); hiç açık gün
 * yoksa dündür. Kontrol listesi ucu geçici Z karşılaştırmalarını TAZELEDİĞİ için (yazma yapar) sorgusu
 * `staleTime: 0` ile çalışır ve her mutasyondan sonra geçersiz kılınır.
 */
function DayEndPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftDayEnd")}
      description={t("shifts.dayEnd.description")}
      breadcrumb={[{ label: t("menu.shiftDayEnd") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <DayEndBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function DayEndBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useShiftPermissions();
  const station = useShiftStation(companyId);
  const [closing, setClosing] = useState(false);

  const setDate = (date: string) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, date })) as never,
      replace: true,
    });

  // Varsayılan gün: açık günlerin EN ESKİSİ. Sayfa boyu bilinçli olarak büyüktür (gün listesi kısa).
  const openDays = useBusinessDays(companyId, {
    stationId: station.stationId ?? undefined,
    status: "OPEN",
    limit: 100,
  });
  const recentDays = useBusinessDays(companyId, {
    stationId: station.stationId ?? undefined,
    limit: 10,
  });
  const today = toIstanbulDate(new Date());
  // Kontrol listesi ucu YAZMA yapar (geçici Z karşılaştırmalarını tazeler): açık günler yüklenmeden
  // "dün" ile boşuna bir çağrı atılmaz; tarih ancak URL'den ya da açık gün listesinden çözülünce istenir.
  const businessDate =
    search.date ?? (openDays.data ? defaultBusinessDate(openDays.data.items, today) : null);
  const checklist = useDayChecklist(companyId, station.stationId, businessDate);

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  const view = checklist.data;
  const blocking = view ? blockingChecks(view.checklist.items) : [];

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <ShiftStationField selection={station} />
        <FormField label={t("shifts.fields.businessDate")} className="w-44">
          {(control) => (
            <DateInput {...control} value={businessDate} onValueChange={(value) => value && setDate(value)} />
          )}
        </FormField>
        {openDays.data && openDays.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("shifts.dayEnd.noOpenDays")}</p>
        ) : null}
      </Card>

      {checklist.error ? (
        <Alert tone="danger">{errorMessage(checklist.error)}</Alert>
      ) : !view ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <DayHeaderCard
            view={view}
            actions={
              <>
                {view.status === "CLOSED" && view.dayClosingId !== null ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/vardiya/gun-sonu/$id" params={{ id: view.dayClosingId }}>
                      {t("shifts.dayEnd.openLockedDay")}
                    </Link>
                  </Button>
                ) : permissions.canCloseDay ? (
                  <Button
                    size="sm"
                    disabled={!view.checklist.canClose}
                    onClick={() => setClosing(true)}
                    title={view.checklist.canClose ? undefined : t("shifts.dayEnd.cannotClose")}
                  >
                    <Lock aria-hidden="true" />
                    {t("shifts.dayEnd.closeAction")}
                  </Button>
                ) : null}
                {view.status !== "CLOSED" && !view.checklist.canClose ? (
                  <span className="self-center text-sm text-muted-foreground">
                    {t("shifts.dayEnd.blockedBy", {
                      items: blocking.map((item) => item.label).join(" · ") || t("shifts.dayEnd.unknown"),
                    })}
                  </span>
                ) : null}
              </>
            }
          />

          <ChecklistCard
            items={view.checklist.items}
            canClose={view.checklist.canClose}
            stationId={view.stationId}
            businessDate={view.businessDate}
            onSelectDate={setDate}
          />

          <DayTotalsPanel totals={view.totals} />
          <DayShiftsPanel shifts={view.shifts} />
          <DayZReportsPanel
            zReports={view.zReports}
            stationId={view.stationId}
            businessDate={view.businessDate}
          />

          <RecentDays
            rows={recentDays.data?.items ?? []}
            isPending={recentDays.isPending}
            error={recentDays.error}
            onSelect={setDate}
          />

          <CloseDayDialog
            open={closing}
            onOpenChange={setClosing}
            companyId={companyId}
            stationId={view.stationId}
            stationName={view.stationName}
            businessDate={view.businessDate}
            onClosed={(detail) => void navigate({ to: "/vardiya/gun-sonu/$id", params: { id: detail.id } })}
          />
        </>
      )}
    </>
  );
}

function RecentDays({
  rows,
  isPending,
  error,
  onSelect,
}: {
  rows: readonly {
    id: string;
    businessDate: string;
    status: "OPEN" | "CLOSED";
    shiftCount: number;
    expectedAmount: string;
    shortage: string;
    surplus: string;
    salesLedgerStatus: "NONE" | "POSTED" | "QUEUED";
  }[];
  isPending: boolean;
  error: unknown;
  onSelect: (businessDate: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  if (error) return <Alert tone="danger">{errorMessage(error)}</Alert>;
  if (isPending) return <SkeletonRows rows={4} />;
  return (
    <SimpleTable
      caption={t("shifts.dayEnd.recentCaption")}
      head={
        <tr>
          <th scope="col" className={thClass}>
            {t("shifts.fields.businessDate")}
          </th>
          <th scope="col" className={thClass}>
            {t("common.status")}
          </th>
          <th scope="col" className={thRightClass}>
            {t("shifts.dayEnd.fields.shiftCount")}
          </th>
          <th scope="col" className={thRightClass}>
            {t("shifts.closing.expected")}
          </th>
          <th scope="col" className={thClass}>
            {t("shifts.dayEnd.fields.ledger")}
          </th>
          <th scope="col" className={thClass}>
            {t("common.actions")}
          </th>
        </tr>
      }
    >
      {rows.length === 0 ? (
        <tr className="border-t">
          <td className={tdClass} colSpan={6}>
            {t("shifts.dayEnd.noRecentDays")}
          </td>
        </tr>
      ) : (
        rows.map((row) => (
          <tr key={row.id} className="border-t">
            <td className={`${tdClass} tabular`}>{formatDate(row.businessDate)}</td>
            <td className={tdClass}>
              <StatusBadge tone={row.status === "CLOSED" ? "neutral" : "info"}>
                {DAY_CLOSING_STATUS_LABELS[row.status]}
              </StatusBadge>
            </td>
            <td className={tdRightClass}>{row.shiftCount}</td>
            <td className={tdRightClass}>
              <MoneyText value={row.expectedAmount} />
            </td>
            <td className={tdClass}>
              <StatusBadge tone={DAY_LEDGER_TONE[row.salesLedgerStatus]}>
                {SHIFT_LEDGER_STATUS_LABELS[row.salesLedgerStatus]}
              </StatusBadge>
            </td>
            <td className={tdClass}>
              <span className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => onSelect(row.businessDate)}>
                  {t("shifts.dayEnd.selectDay")}
                </Button>
                {row.status === "CLOSED" ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/vardiya/gun-sonu/$id" params={{ id: row.id }}>
                      {t("shifts.dayEnd.openLockedDay")}
                    </Link>
                  </Button>
                ) : null}
              </span>
            </td>
          </tr>
        ))
      )}
    </SimpleTable>
  );
}
